import React, { useEffect, useState } from "react";
import { Row, Col, Form, Select, Input, Button, Table, InputNumber, Modal } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import voucherExample from '../../../images/CODIGOVOUCHER.png';
import receiptExample from '../../../images/CODIGOBOLETA.png';
import invoiceService from '../../../services/invoiceService';
import { formatAmountByCurrency, formatDate } from "../../../helpers";
import * as _ from 'lodash';

const authorizationCodeHelp = (
  <React.Fragment>
    <p className="mb-30">
      Aparece como <span className="text-bold">AUTORIZACIÓN</span> o <span className="text-bold">CÓDIGO DE AUTORIZACIÓN</span> en el comprobante
      de la máquina Transbank o en la boleta electrónica. Son hasta 6 caracteres, entre letras
      y números.
    </p>
    <Row gutter={24}>
      <Col span={12}>
        <p className="text-bold primary-color">Comprobante de venta</p>
        <img
          src={voucherExample}
          alt="Código de autorización en el comprobante de venta"
          style={{ width: '100%' }}
        />
      </Col>
      <Col span={12}>
        <p className="text-bold primary-color">Boleta electrónica</p>
        <img
          src={receiptExample}
          alt="Código de autorización en la boleta electrónica"
          style={{ width: '100%' }}
        />
      </Col>
    </Row>
  </React.Fragment>
);

const Payments = ({
  countryId,
  isConfirmation,
  type,
  payments,
  setPayments,
  setPaymentIsValid,
  prestations
}) => {
  const [paymentMethods, setPaymentMethods] = useState();
  const [requiresAuthorizationCode, setRequiresAuthorizationCode] = useState(false);
  const [showAuthorizationCodeHelp, setShowAuthorizationCodeHelp] = useState(false);
  const [isCheckingAuthorizationCode, setIsCheckingAuthorizationCode] = useState(false);
  const [paymentform] = Form.useForm()
  const [prestation] = useState(prestations[0])

  const pushPayment = (payment) => {
    let items = payments;

    items.push(payment);

    setPayments([...items]);
    paymentform.resetFields();
    setRequiresAuthorizationCode(false);
    paymentsTotalValidation(items);
  }

  const fetchInvoicesWithSameAuthorizationCode = async (payment) => {
    setIsCheckingAuthorizationCode(true);

    try {
      const response = await invoiceService.fetchAuthorizationCodeUsage({
        countryId,
        authorizationCode: payment['authorizationCode']
      });
      return response.invoices || [];
    } catch (error) {
      console.warn('No se pudo verificar el uso previo del código de autorización', error);
      return [];
    } finally {
      setIsCheckingAuthorizationCode(false);
    }
  }

  const addPayments = async (values) => {
    const payment = {
      payment: values['payment'],
      amount: parseFloat(values['amount'])
    };

    if (values['authorizationCode']) {
      payment['authorizationCode'] = values['authorizationCode'].trim();
    }

    if (!payment['authorizationCode']) {
      pushPayment(payment);
      return;
    }

    const usedInInvoices = await fetchInvoicesWithSameAuthorizationCode(payment);

    if (usedInInvoices.length === 0) {
      pushPayment(payment);
      return;
    }

    Modal.confirm({
      title: <span className="text-bold">Este código de autorización ya se utilizó</span>,
      icon: null,
      width: 520,
      style: { top: 180 },
      className: 'authorization-code-warning',
      content: (
        <React.Fragment>
          <p>
            El código <span className="text-bold">{payment['authorizationCode']}</span> se registró para las siguientes facturas:
          </p>
          <ul>
            {usedInInvoices.map((invoice) => (
              <li key={invoice.number}>
                Factura {invoice.number}{invoice.emissionDate ? ` (${formatDate(invoice.emissionDate)})` : ''}
              </li>
            ))}
          </ul>
          <p>Revisa el comprobante antes de continuar. ¿Quieres agregar este método de pago de todas formas?</p>
        </React.Fragment>
      ),
      okText: 'Agregar',
      cancelText: 'Cancelar',
      onOk: () => pushPayment(payment)
    });
  }

  const getPaymentName = (value) => {
    const payment = paymentMethods.find((item) => item.id == value);
    return payment['name'];
  }

  const fetchPaymentMethods = async () => {
    const response = await invoiceService.fetchExternalPaymentMethods({ countryId });
    setPaymentMethods(response);
  };

  const paymentsTotalValidation = async (items) => {
    const sum = _.sumBy(items, 'amount');
    setPaymentIsValid(sum == prestation['total']);
  }

  const handlePayments = (paymentId) => {
    const paymentMethod = paymentMethods.find((item) => item.id == paymentId);
    setRequiresAuthorizationCode(Boolean(paymentMethod && paymentMethod.requiresAuthorizationCode));
    paymentform.setFieldsValue({ authorizationCode: undefined });
  }

  useEffect(() => {
    fetchPaymentMethods();
  }, [])

  return (
    <React.Fragment>
      {
        type !== "agreement" && isConfirmation && (
          <Col lg={24}>
            <Form form={paymentform} onFinish={addPayments} layout="vertical">
              <h2>Método de pago</h2>

              <Row gutter={10}>
                <Col lg={6}>
                  <Form.Item
                    name="payment"
                    rules={[{ required: true, message: 'Este campo es requerido' }]}
                  >
                    <Select placeholder="Método de pago" onChange={handlePayments}>
                      {
                        paymentMethods && paymentMethods.map(payment => {
                          return <Select.Option value={payment.id}>{payment.name}</Select.Option>
                        })
                      }
                    </Select>
                  </Form.Item>
                </Col>
                <Col lg={4}>
                  <Form.Item
                    name="amount"
                    rules={[{ required: true, message: 'El importe es requerido' }]}
                  >
                    <InputNumber
                      placeholder="Importe"
                      style={{ width: "100%" }}
                      min={1}
                    />
                  </Form.Item>
                </Col>
                <Col lg={4}>
                  {
                    requiresAuthorizationCode && (
                      <Form.Item
                        name="authorizationCode"
                        rules={[
                          { required: true, message: 'El código de autorización es requerido' },
                          { pattern: /^[a-zA-Z0-9]{1,6}$/, message: 'Solo caracteres alfanuméricos' }
                        ]}
                      >
                        <Input
                          placeholder="Código de autorización"
                          maxLength={6}
                          suffix={
                            <Button
                              type="text"
                              shape="circle"
                              size="small"
                              aria-label="Mostrar ayuda sobre el código de autorización"
                              icon={<QuestionCircleOutlined style={{ fontSize: 16, color: "#505050" }} />}
                              onClick={() => setShowAuthorizationCodeHelp(true)}
                            />
                          }
                        />
                      </Form.Item>
                    )
                  }
                </Col>
                <Col lg={4}>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={isCheckingAuthorizationCode}>
                      {isCheckingAuthorizationCode ? 'Verificando código...' : 'Agregar'}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>

            </Form>

            <Modal
              title={<span className="text-bold primary-color">¿Dónde encuentro el código de autorización?</span>}
              visible={showAuthorizationCodeHelp}
              onCancel={() => setShowAuthorizationCodeHelp(false)}
              footer={null}
              width={800}
            >
              {authorizationCodeHelp}
            </Modal>

            <Table
              dataSource={payments}
              columns={[
                {
                  title: 'Monto',
                  dataIndex: 'amount',
                  key: 'amount',
                  render: (text, record) => {
                    return (
                      <span>
                        {formatAmountByCurrency({
                          countryCode: prestation.currency.locate_code,
                          currencyCode: prestation.currency.code,
                          amount: text
                        })}
                      </span>
                    )
                  }

                },
                {
                  title: 'Metodo de pago',
                  dataIndex: 'payment',
                  key: 'payment',
                  render: (text, record) => {
                    return <span>
                      {getPaymentName(text)}
                    </span>
                  }
                },
                {
                  title: 'Código de autorización',
                  dataIndex: 'authorizationCode',
                  key: 'authorizationCode',
                  render: (text) => {
                    return <span>{text || '-'}</span>
                  }
                },
                {
                  title: '-',
                  dataIndex: '',
                  key: '',
                  render: (text, record) =>
                    <Button title="Sure to delete?" onClick={() => {
                      setPayments(_.filter(payments, (item) => {
                        return item.payment != record.payment;
                      }));
                    }}>
                      Quitar
                    </Button>
                },
              ]}
              pagination={false}
            />
          </Col>
        )
      }
    </React.Fragment>
  )
}

export default Payments;